import { useEffect, useState } from "react";
import {
  Phone,
  Smartphone,
  FileText,
  FileCheck2,
  Home,
  CalendarDays,
  RotateCw,
  Sparkles,
  Users,
  LogOut,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/utils";
import { ENVIRONMENT } from "@/config";
import { auth, logOut } from "@/lib/firebase";

const NAV_ITEMS = [
  { title: "Dashboard", path: "/", icon: Home },
  { title: "PBM directory", path: "/payer-directory", icon: Building2 },
  { title: "Daily Intake", path: "/intake", icon: CalendarDays },
  { title: "Retry Console", path: "/retry", icon: RotateCw },
  { title: "SO Rerun", path: "/structured-output-rerun", icon: Sparkles },
  { title: "Patient Registry", path: "/patients", icon: Users },
  { title: "Last Calls", path: "/calls", icon: Phone },
  { title: "Phone numbers", path: "/phone-numbers", icon: Smartphone },
  { title: "Case Records", path: "/cases", icon: FileText },
  { title: "VOB Ready", path: "/vob-ready", icon: FileCheck2 },
];

const ENV_COLORS: Record<string, string> = {
  DEVELOPMENT: "bg-blue-100 text-blue-800",
  TEST: "bg-yellow-100 text-yellow-800",
  PRODUCTION: "bg-red-100 text-red-800",
};

function SidebarCollapseButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={onToggle}
          tooltip={label}
          aria-label={label}
          className="justify-center"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, toggleSidebar, isMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const [user, setUser] = useState<User | null>(auth.currentUser);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return () => unsub();
  }, []);

  const initials = (user?.displayName || user?.email || "?")
    .split(/[ @]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b px-4 py-3">
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <span className="text-lg font-bold tracking-tight group-data-[collapsible=icon]:hidden">
            Asepha Call
          </span>
          <span
            className="hidden size-8 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-sm font-bold group-data-[collapsible=icon]:flex"
            title="Asepha Call"
          >
            A
          </span>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium group-data-[collapsible=icon]:hidden",
              ENV_COLORS[ENVIRONMENT] || ENV_COLORS.DEVELOPMENT,
            )}
          >
            {ENVIRONMENT}
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Dashboard</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    isActive={
                      item.path === "/"
                        ? location.pathname === "/"
                        : location.pathname.startsWith(item.path)
                    }
                    onClick={() => navigate(item.path)}
                    tooltip={item.title}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      {user && (
        <SidebarFooter className="border-t p-2">
          <div className="flex items-center gap-2 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="h-7 w-7 shrink-0 rounded-full border"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                {initials}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate text-xs font-medium">
                {user.displayName || user.email}
              </span>
              {user.displayName && (
                <span className="truncate text-[11px] text-muted-foreground">
                  {user.email}
                </span>
              )}
            </div>
            <button
              onClick={() => logOut().catch(() => {})}
              title="Sign out"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground group-data-[collapsible=icon]:hidden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          {!isMobile ? (
            <SidebarCollapseButton collapsed={collapsed} onToggle={toggleSidebar} />
          ) : null}
        </SidebarFooter>
      )}
      {!user && !isMobile ? (
        <SidebarFooter className="border-t p-2">
          <SidebarCollapseButton collapsed={collapsed} onToggle={toggleSidebar} />
        </SidebarFooter>
      ) : null}
    </Sidebar>
  );
}
